import SaveYourLookPill from "@/components/native/SaveYourLookPill";
import LooksSavedPill from "@/components/native/LooksSavedPill";
import { shortHandle } from "@/lib/guestHandle";
import UnsavedLookPreviewCard, {
  type UnsavedOutfitLite,
} from "@/components/native/UnsavedLookPreviewCard";
import RecentAnalysesList, {
  type RecentAnalysisRow,
} from "@/components/native/RecentAnalysesList";
// Wave 4: legacy progress card removed — replaced by pre-signup
// 3-screen value walkthrough (see src/components/onboarding/).
export interface OnboardingProfile {
  personalisation_enabled?: boolean | null;
}
import HomeWardrobeCtaCard from "@/components/native/HomeWardrobeCtaCard";
import PersonalisationUpsellCard from "@/components/native/PersonalisationUpsellCard";
import HomeDemoCard from "@/components/native/HomeDemoCard";

const INK = "#2C2820";
const MUTED = "#6B6560";
const DIM = "#9B958E";
const CREAM = "#FAECE7";
const CORAL = "#C84B2F";
const BORDER = "#F1EFE8";
const TOP_BG = "#E8E0D5";

interface NativeHomeProps {
  isAuthenticated: boolean;
  guestHandle?: string | null;
  profile: OnboardingProfile | null;
  recentAnalyses: RecentAnalysisRow[];
  unsavedGuestAnalysis: UnsavedOutfitLite | null;
  unsavedGuestCount: number;
  savedLooksCount: number;
  wardrobeItemsCount: number;
  lb3WardrobeCount: number | null;
  onSignInClick: () => void;
  onAddOutfitClick: () => void;
  onUploadScreenshotClick: () => void;
  onUnsavedPreviewTap: () => void;
  onUnsavedRetry: (outfitId: string) => void;
  onRecentTap: (id: string) => void;
  onSavedPillTap: () => void;
  onCardBCtaTap: () => void;
  onPersonalisationUpsellTap: () => void;
  onTryDemoTap: () => void;
  shoppingSection?: React.ReactNode;
}

function TopNav({
  right,
}: {
  right: React.ReactNode;
}) {
  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        background: TOP_BG,
        paddingTop: "calc(var(--safe-top, 0px) + 12px)",
        paddingLeft: 16,
        paddingRight: 16,
        paddingBottom: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <span
        className="l2f-type-label"
        style={{
          color: INK,
          letterSpacing: "-0.02em",
        }}
      >
        Like2Fit
      </span>
      <div>{right}</div>
    </header>
  );
}

function SignInPill({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "rgba(255,255,255,0.5)",
        border: "1px solid rgba(0,0,0,0.07)",
        borderRadius: 20,
        padding: "6px 14px",
        fontSize: 13,
        fontWeight: 500,
        color: "#4A4540",
        cursor: "pointer",
        minHeight: 44,
      }}
    >
      Sign in
    </button>
  );
}

function EmptyHomeHero({
  onAddOutfitClick,
  onUploadScreenshotClick,
  guestHandle,
}: {
  onAddOutfitClick: () => void;
  onUploadScreenshotClick: () => void;
  guestHandle?: string | null;
}) {
  return (
    <div style={{ padding: "24px 16px" }}>
      <h1
        className="l2f-type-h1"
        style={{
          color: INK,
          letterSpacing: "-0.02em",
          marginBottom: 6,
          margin: 0,
        }}
      >
        Know before you buy
      </h1>
      {guestHandle && (
        <p
          style={{
            margin: "4px 0 0",
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontStyle: "italic",
            fontSize: 13,
            color: DIM,
          }}
        >
          {guestHandle}
        </p>
      )}
      <p
        className="l2f-type-secondary"
        style={{
          color: MUTED,
          marginTop: 6,
          marginBottom: 24,
        }}
      >
        Paste any outfit and we'll show you how it looks on you. No signup needed.
      </p>

      <button
        type="button"
        onClick={onAddOutfitClick}
        style={{
          display: "block",
          width: "100%",
          background: CREAM,
          border: `1.5px solid ${CORAL}`,
          borderRadius: 16,
          padding: 14,
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 10,
          }}
        >
          <i className="ti ti-brand-tiktok" style={{ fontSize: 24, color: INK }} />
          <i className="ti ti-brand-instagram" style={{ fontSize: 24, color: INK }} />
          <i className="ti ti-brand-pinterest" style={{ fontSize: 24, color: INK }} />
        </div>
        <div
          className="l2f-type-label"
          style={{
            color: INK,
            marginBottom: 4,
          }}
        >
          Share from social media
        </div>
        <div className="l2f-type-caption" style={{ color: MUTED }}>
          See any outfit on TikTok, Instagram or Pinterest? Tap share and select Like2Fit.
        </div>
      </button>

      <div style={{ textAlign: "center", marginTop: 16 }}>
        <button
          type="button"
          onClick={onUploadScreenshotClick}
          style={{
            background: "transparent",
            border: "none",
            color: "#993C1D",
            fontSize: 13,
            fontWeight: 500,
            textDecoration: "underline",
            cursor: "pointer",
            padding: "12px 8px",
            minHeight: 44,
          }}
        >
          or upload a screenshot
        </button>
      </div>
      <p
        className="l2f-type-caption"
        style={{
          color: DIM,
          textAlign: "center",
          marginTop: 8,
        }}
      >
        Works with TikTok, Instagram, Pinterest and more
      </p>
    </div>
  );
}

function CompactAddRow({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 16px",
        background: "transparent",
        border: "none",
        borderBottom: `0.5px solid ${BORDER}`,
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: 999,
          background: CORAL,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <i className="ti ti-plus" style={{ fontSize: 18, color: "#FFFFFF" }} />
      </span>
      <span style={{ fontSize: 13, fontWeight: 600, color: INK }}>
        Add another outfit
      </span>
    </button>
  );
}

export default function NativeHome({
  isAuthenticated,
  guestHandle,
  profile,
  recentAnalyses,
  unsavedGuestAnalysis,
  unsavedGuestCount,
  savedLooksCount,
  wardrobeItemsCount,
  lb3WardrobeCount,
  onSignInClick,
  onAddOutfitClick,
  onUploadScreenshotClick,
  onUnsavedPreviewTap,
  onUnsavedRetry,
  onRecentTap,
  onSavedPillTap,
  onCardBCtaTap,
  onPersonalisationUpsellTap,
  onTryDemoTap,
  shoppingSection,
}: NativeHomeProps) {
  // Resolve state
  const hasRecents = recentAnalyses.length > 0;
  const guestWithLook = !isAuthenticated && !!unsavedGuestAnalysis;
  const guestEmpty = !isAuthenticated && !guestWithLook;

  const cardBState: "guest" | "auth-empty" | "auth-sparse" = !isAuthenticated
    ? "guest"
    : lb3WardrobeCount === 0
      ? "auth-empty"
      : "auth-sparse";

  let rightPill: React.ReactNode = null;
  if (guestEmpty) {
    rightPill = <SignInPill onClick={onSignInClick} />;
  } else if (guestWithLook) {
    rightPill = <SaveYourLookPill onClick={onUnsavedPreviewTap} />;
  } else if (isAuthenticated && hasRecents && savedLooksCount > 0) {
    rightPill = (
      <LooksSavedPill count={savedLooksCount} onClick={onSavedPillTap} />
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FFFCF8",
        paddingBottom: "calc(49px + var(--safe-bottom) + 12px)",
      }}
    >
      <TopNav right={rightPill} />

      {guestWithLook && unsavedGuestAnalysis && (
        <div style={{ paddingTop: 16 }}>
          {guestHandle && (
            <p
              style={{
                margin: "0 16px 8px",
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                fontSize: 12,
                color: DIM,
              }}
            >
              {shortHandle(guestHandle)}
            </p>
          )}
          <UnsavedLookPreviewCard
            outfit={unsavedGuestAnalysis}
            unsavedCount={unsavedGuestCount}
            onTap={onUnsavedPreviewTap}
            onRetry={onUnsavedRetry}
          />
        </div>
      )}

      {hasRecents && <CompactAddRow onClick={onAddOutfitClick} />}

      {shoppingSection}

      {hasRecents && (
        <RecentAnalysesList
          analyses={recentAnalyses}
          onAnalysisTap={onRecentTap}
        />
      )}

      <EmptyHomeHero
        onAddOutfitClick={onAddOutfitClick}
        onUploadScreenshotClick={onUploadScreenshotClick}
        guestHandle={guestEmpty ? guestHandle : undefined}
      />

      {!isAuthenticated && <HomeDemoCard onTap={onTryDemoTap} />}

      <HomeWardrobeCtaCard state={cardBState} onCtaTap={onCardBCtaTap} />

      {isAuthenticated && profile && profile.personalisation_enabled !== true && (
        <PersonalisationUpsellCard onTap={onPersonalisationUpsellTap} />
      )}

    </div>
  );
}
